// src/App.tsx
import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";

import Welcome from "./Pages/Welcome";
import EditorPage from "./Pages/EditorPage";
import DocumentsPage from "./Pages/DocumentsPage"; // ✅ correct file name
import OrganizerPage from "./Pages/OrganizerPage";
import MobileOrganizerDashboard from "./Pages/MobileOrganizerDashboard";
import VirtualRoomNotesPage from "./Pages/VirtualRoomNotesPage";
import JournalPage from "./Pages/JournalPage";
import MessagingView from "./Pages/MessagingView";
import { CalendarView } from "./Pages/CalendarView";
import OrganizerLanding from "./Pages/OrganizerLanding";
import OrganizerSignIn from "./Pages/OrganizerSignIn";
import OrganizerSignUp from "./Pages/OrganizerSignUp";
import OnboardingPage from "./Pages/OnboardingPage";
import AuthCallback from "./Pages/AuthCallback";
import SettingsPage from "./Pages/SettingsPage";
import AdminPage from "./Pages/AdminPage";
import AdminBackendServices from "./Pages/AdminBackendServices";
import AdminSignIn from "./Pages/AdminSignIn";
import { AdminSuggestionsPage } from "./Pages/AdminSuggestionsPage";
import { BroadcastNotificationPage } from "./Pages/BroadcastNotificationPage";
import { AdminBroadcastPage } from "./Pages/AdminBroadcastPage";
import TermsOfServicePage from "./Pages/TermsOfServicePage";
import PrivacyPolicyPage from "./Pages/PrivacyPolicyPage";
import ParentalConsentPage from "./Pages/ParentalConsentPage";
import PaymentSuccessPage from "./components/PaymentSuccessPage";
import AdminActivateAccount from "./Pages/AdminActivateAccount";
import AdminDebugDatabase from "./Pages/AdminDebugDatabase";
import AdminPasswordResetPage from "./Pages/AdminPasswordResetPage";
import ResourcesPage from "./Pages/ResourcesPage";
import ForgotPasswordPage from "./Pages/ForgotPasswordPage";
import ResetPasswordPage from "./Pages/ResetPasswordPage";
import LogoutPage from "./Pages/LogoutPage";
import PricingPage from "./Pages/PricingPage";
import WhiptSummPage from "./Pages/WhiptSummPage"; // WHIPT SUMM!
import NotificationSettingsPage from "./Pages/NotificationSettingsPage";
import BetaNoticeBanner from "./components/BetaNoticeBanner";
import { FeatureGate } from "@/components/FeatureGate";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { GuestRoute } from "@/components/GuestRoute";
import MusicPlayerDock from "@/components/MusicPlayerDock";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import GuestAccessPage from "./Pages/GuestAccessPage";
import GuestGoodbyePage from "./Pages/GuestGoodbyePage";
import { NotificationProvider } from "@/components/NotificationProvider";
import { PlatformLayout } from "@/components/PlatformLayout";

// Device detection via user agent
function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// Redirect to /m/organizer or /d/organizer based on user agent
const OrganizerDispatcher = () => {
  const target = isMobileDevice() ? '/m/organizer' : '/d/organizer';
  return <Navigate to={target} replace />;
};

// Conditional Beta Banner - only show on sign-in/sign-up pages
const ConditionalBetaBanner = () => {
  const location = useLocation();
  const showBanner = location.pathname === '/organizer/sign-in' || 
                     location.pathname === '/organizer/sign-up';
  
  return showBanner ? <BetaNoticeBanner /> : null;
};

// Conditional Music Player - only show on organizer dashboard home screen
const ConditionalMusicPlayer = () => {
  const location = useLocation();
  const [showMusic, setShowMusic] = React.useState(false); // START HIDDEN
  const [isReady, setIsReady] = React.useState(false); // Double gate
  
  // AGGRESSIVE SHOW LOGIC: Only show when EXPLICITLY safe
  React.useEffect(() => {
    // Force hide initially on auth pages
    if (location.pathname.includes('/sign-in') || 
        location.pathname.includes('/sign-up') || 
        location.pathname.includes('/auth/') ||
        location.pathname.includes('/onboarding') ||
        location.pathname === '/' ||
        location.pathname === '/welcome') {
      setShowMusic(false);
      setIsReady(false);
      return;
    }
    
    const checkSafeToShow = () => {
      // MUST be on dashboard (either platform)
      const isOnDashboard = location.pathname === '/organizer' ||
                            location.pathname === '/m/organizer' ||
                            location.pathname === '/d/organizer';
      
      // MUST NOT have virtual room open
      const isVirtualRoomOpen = sessionStorage.getItem('virtualRoomOpen') === 'true';
      
      // MUST NOT have modal/overlay elements
      const hasModal = document.querySelector('[role="dialog"]') !== null;
      const hasOverlay = document.querySelector('.fixed.inset-0') !== null;
      const hasZIndex = document.querySelector('[style*="z-index: 9999"]') !== null;
      
      // Only show if ALL conditions are safe
      const isSafeToShow = isOnDashboard && 
                          !isVirtualRoomOpen && 
                          !hasModal && 
                          !hasOverlay && 
                          !hasZIndex;
      
      setShowMusic(isSafeToShow);
    };
    
    // Wait longer before first check to ensure DOM is settled
    const readyTimeout = setTimeout(() => setIsReady(true), 300);
    const checkTimeout = setTimeout(checkSafeToShow, 500);
    
    // Check frequently for any changes
    const interval = setInterval(checkSafeToShow, 500);
    
    // Also listen for DOM mutations
    const observer = new MutationObserver(checkSafeToShow);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      clearTimeout(readyTimeout);
      clearTimeout(checkTimeout);
      clearInterval(interval);
      observer.disconnect();
    };
  }, [location.pathname]);
  
  // Double gate: must be ready AND safe to show
  return (isReady && showMusic) ? <MusicPlayerDock side="right" /> : null;
};

const App = () => {
  return (
    <BrowserRouter>
      {/* Temporarily re-enabled for debugging */}
      <NotificationProvider>
        <ConditionalBetaBanner />
        <ConditionalMusicPlayer />
      <PlatformLayout>
      <Routes>
        <Route path="/" element={<OrganizerLanding />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/guest" element={<GuestAccessPage />} />
        <Route path="/guest/goodbye" element={<GuestGoodbyePage />} />
        <Route path="/guest-goodbye" element={<GuestGoodbyePage />} />
        <Route
          path="/guest/organizer"
          element={
            <GuestRoute>
              <FeatureGate feature="organizer">
                <OrganizerPage />
              </FeatureGate>
            </GuestRoute>
          }
        />
        
        <Route
          path="/editor"
          element={
            <FeatureGate feature="thesiscribe">
              <EditorPage />
            </FeatureGate>
          }
        />
        <Route
          path="/documents"
          element={
            <FeatureGate feature="thesiscribe">
              <DocumentsPage />
            </FeatureGate>
          }
        />
        <Route path="/organizer/sign-in" element={<OrganizerSignIn />} />
        <Route path="/organizer/sign-up" element={<OrganizerSignUp />} />
        <Route path="/organizer/onboarding" element={<OnboardingPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route 
          path="/organizer/select" 
          element={
            <ProtectedRoute>
              {/* Organizer selection is temporarily bypassed; keep import removed. */}
              <Navigate to="/" />
            </ProtectedRoute>
          } 
        />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        {/* <Route path="/refund" element={<RefundPolicyPage />} /> */}
        <Route path="/parental-consent" element={<ParentalConsentPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/logout" element={<LogoutPage />} />
        
        {/* Admin routes - For development/emergency access only */}
        <Route path="/admin/activate" element={<AdminActivateAccount />} />
        <Route path="/admin/reset-password" element={<AdminPasswordResetPage />} />
        <Route path="/admin/debug" element={<AdminDebugDatabase />} />
        <Route path="/admin/resources" element={<ResourcesPage />} />
        <Route path="/admin/suggestions" element={<AdminSuggestionsPage />} />
        <Route path="/admin/broadcast" element={<BroadcastNotificationPage />} />
        <Route path="/admin/backend-services" element={<AdminBackendServices />} />
        <Route path="/admin/sign-in" element={<AdminSignIn />} />
        <Route path="/admin" element={<AdminPage />} />
        
        <Route
          path="/organizer/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizer/settings/notifications"
          element={
            <ProtectedRoute>
              <NotificationSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizer/journal"
          element={
            <ProtectedRoute>
              <FeatureGate feature="organizer">
                <JournalPage />
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizer/calendar"
          element={
            <ProtectedRoute>
              <FeatureGate feature="organizer">
                <CalendarView />
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizer/documents"
          element={
            <ProtectedRoute>
              <FeatureGate feature="organizer">
                <DocumentsPage />
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/whipt-summ"
          element={
            <ProtectedRoute>
              <WhiptSummPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/test-docs"
          element={
            <div style={{ padding: '20px', color: 'white', background: '#1a1a2e', minHeight: '100vh' }}>
              <h1>TEST PAGE WORKS!</h1>
              <p>If you see this, routing is working.</p>
              <button onClick={() => window.location.href = '/organizer'}>Go Back</button>
            </div>
          }
        />
        <Route
          path="/organizer/messages"
          element={
            <ProtectedRoute>
              <FeatureGate feature="organizer">
                <MessagingView />
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/m/organizer"
          element={
            <ProtectedRoute>
              <FeatureGate feature="organizer">
                <MobileOrganizerDashboard />
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/d/organizer"
          element={
            <ProtectedRoute>
              <FeatureGate feature="organizer">
                <OrganizerPage />
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizer"
          element={
            <ProtectedRoute>
              <FeatureGate feature="organizer">
                <OrganizerDispatcher />
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizer/virtual-room"
          element={
            <ProtectedRoute>
              <FeatureGate feature="organizer">
                <VirtualRoomNotesPage />
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizer/personal"
          element={
            <ProtectedRoute>
              <FeatureGate feature="organizer">
                {/* Personal organizer temporarily disabled */}
                <Navigate to="/" />
              </FeatureGate>
            </ProtectedRoute>
          }
        />
      </Routes>
      </PlatformLayout>
      </NotificationProvider>
    </BrowserRouter>
  );
};

export default App;